import { type ReactElement, useState, useCallback } from 'react';
import { useFloating, offset, flip, size, autoUpdate } from '@floating-ui/react';
import { Chip, ButtonAlt } from '@optimization/ssi-common';
import { type UpdateQueryParameterValues } from '../../hooks/use-update-query-parameter-values';
import styles from './style.module.scss';
import useOutsideClick from './hooks/use-outside-click';
import Filters from './Filters';

export interface HeaderProperties {
  readonly selectedBatteries: string[];
  readonly selectedConfigurations: string[];
  readonly selectedVehicleTypes: string[];
  readonly selectedPropulsions: string[];
  readonly batteries: string[];
  readonly configurations: string[];
  readonly showAlerts: boolean;
  readonly updateQueryParameterValues: UpdateQueryParameterValues;
}

const FilterMenu = ({
  selectedBatteries,
  selectedConfigurations,
  selectedVehicleTypes,
  selectedPropulsions,
  batteries,
  configurations,
  showAlerts,
  updateQueryParameterValues,
}: HeaderProperties): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);

  const outsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const wrapperReference = useOutsideClick(outsideClick);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(24),
      flip(),
      size({
        apply({ elements }) {
          Object.assign(elements.floating.style);
        },
      }),
    ],
    open: isOpen,
    whileElementsMounted: autoUpdate,
    onOpenChange: setIsOpen,
  });

  const setReferenceNode = useCallback(
    (node: HTMLTdsChipElement | null) => {
      refs.setReference(node);
    },
    [refs],
  );

  const setFloatingNode = useCallback(
    (node: HTMLDivElement | null) => {
      refs.setFloating(node);
    },
    [refs],
  );

  return (
    <div className={styles.container} ref={wrapperReference}>
      <div className={styles.chips}>
        <Chip
          text="Alerts"
          size="lg"
          icon="warning"
          checked={showAlerts}
          onClick={() => updateQueryParameterValues({ filterKeys: { showAlerts: [(!showAlerts).toString()] } })}
        />
        <Chip
          text="Filter"
          size="lg"
          icon="filters"
          checked={isOpen}
          onClick={() => setIsOpen((previous) => !previous)}
          ref={setReferenceNode}
        />
      </div>
      {isOpen && (
        /* eslint-disable-next-line react/forbid-dom-props -- Using inline dynamic styles because floating UI requires computed positioning */
        <div className={styles.menu} ref={setFloatingNode} style={floatingStyles}>
          <div className={styles.header}>
            <h3 className={styles.title}>Filter options</h3>
            <ButtonAlt onClick={() => setIsOpen(false)} tdsIcon="cross" />
          </div>
          <div className={styles.content}>
            <Filters
              selectedBatteries={selectedBatteries}
              batteries={batteries}
              configurations={configurations}
              selectedConfigurations={selectedConfigurations}
              selectedVehicleTypes={selectedVehicleTypes}
              selectedPropulsions={selectedPropulsions}
              updateQueryParameterValues={updateQueryParameterValues}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterMenu;
